import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2, ScrollText, CheckCircle2, Circle, Scroll, Sparkles, ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import ParashaContentDialog from "../components/parasha/ParashaContentDialog";

/**
 * AdminAggadot — preview tool for the editorial / rabbinical review
 * round of the curated aggadot. Lists every parasha and lets the
 * reviewer click into a parasha to see the full aggadah card exactly
 * as the public-facing landing page renders it.
 *
 * Behaviour:
 *   • Loads the coverage report (admin-only endpoint).
 *   • Renders a 5-tab navigator (Bereshit / Shemot / Vayikra /
 *     Bamidbar / Devarim) — but flat list is fine, parashot are
 *     already in canonical order.
 *   • Each row shows the parasha name, the source ref, the title,
 *     and a circle/check indicating coverage.
 *   • Clicking a covered parasha expands the AggadahCard inline so
 *     the reviewer can read the source + paraphrase + lesson without
 *     leaving the page.
 *   • Missing parashot are shown in a faded "still to curate" group
 *     at the bottom — no surprises.
 */
export default function AdminAggadot() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [openDialog, setOpenDialog] = useState(null); // { slug, parashaHe, mode } | null

  const loadCoverage = useCallback(() => {
    return api.adminAggadotCoverage()
      .then(setData)
      .catch((err) => {
        if ([401, 403].includes(err?.response?.status)) setUnauthorized(true);
      });
  }, []);

  useEffect(() => {
    loadCoverage().finally(() => setLoading(false));
  }, [loadCoverage]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin text-gold ml-2" strokeWidth={1.5} />
        טוען נתוני כיסוי…
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div dir="rtl" className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
        אין לך הרשאת אדמין לצפות בדף זה.
      </div>
    );
  }

  if (!data) {
    return (
      <div dir="rtl" className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
        לא הצלחנו לטעון את הנתונים. נסה שוב.
      </div>
    );
  }

  const { total, covered, missing, entries, missing_entries, aggadah_en_approved_count, story_en_approved_count } = data;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8" dir="rtl">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        data-testid="admin-aggadot-back"
      >
        <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        חזרה ללוח הבקרה
      </Link>

      <header className="space-y-2 border-b border-gold/20 pb-6">
        <div className="flex items-center gap-3 text-gold">
          <ScrollText className="h-5 w-5" strokeWidth={1.5} />
          <span className="font-heading text-sm tracking-widest uppercase">תצוגה רבנית — ממקורות חז״ל וסיפור לפרשה</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground">
          ממקורות חז״ל וסיפורים — דף הסקירה
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl">
          לחץ על פרשה כדי לפתוח את הכרטיס בדיוק כפי שהוא מוצג למשתמשים בדף הבית.
          התוכן זהה לתצוגה הפומבית — כולל פרפראזה ומסר. הגוון של המסר ("מהמדרש"
          / "פרשנות שלנו") נקבע ע"י המנוע בהתבסס על המקור עצמו.
        </p>
        <div
          data-testid="aggadot-coverage-stats"
          className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm pt-2"
        >
          <span className="text-foreground/80">
            סך פרשות: <span className="font-heading text-gold">{total}</span>
          </span>
          <span className="text-foreground/80">
            עם תוכן: <span className="font-heading text-gold">{covered}</span>
          </span>
          <span className="text-muted-foreground">
            בהמתנה: <span className="font-heading">{missing}</span>
          </span>
          <span className="text-foreground/80" data-testid="aggadot-coverage-en-aggadah-count">
            אנגלית מאושרת (אגדה): <span className="font-heading text-emerald-600">{aggadah_en_approved_count || 0}</span>
          </span>
          <span className="text-foreground/80" data-testid="aggadot-coverage-en-story-count">
            אנגלית מאושרת (סיפור): <span className="font-heading text-emerald-600">{story_en_approved_count || 0}</span>
          </span>
        </div>
      </header>

      {/* Covered parashot — clickable list with TWO action buttons per row:
          "ממקורות חז״ל" and "סיפור". Each opens the same ParashaContentDialog in
          the matching mode, exactly as the public-facing card does. */}
      <section className="space-y-3">
        <h2 className="font-heading text-lg text-foreground">פרשות עם תוכן ({covered})</h2>
        <p className="text-xs text-muted-foreground">
          לכל פרשה: ארבעה כפתורים — תצוגה עברית של "ממקורות חז״ל" / "סיפור" (כבר פומבי), ותצוגת אנגלית של כל אחד מהם לסקירה ואישור. גרסת האנגלית מוסתרת ממשתמשים רגילים עד שתאשר אותה כאן.
        </p>
        <ul className="space-y-2" data-testid="aggadot-covered-list">
          {entries.map((e) => (
            <li
              key={e.slug}
              data-testid={`aggadot-row-${e.slug}`}
              className="border border-gold/15 rounded-sm bg-card px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <CheckCircle2 className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div className="space-y-1 min-w-0">
                  <div className="font-heading text-base text-foreground">פרשת {e.he}</div>
                  <div className="text-xs text-muted-foreground truncate">{e.title_he}</div>
                  <div className="font-latin italic text-[11px] text-muted-foreground">
                    {e.ref_he}
                  </div>
                  {/* EN approval chips — at-a-glance status */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
                    <ApprovalChip
                      label="EN · אגדה"
                      approved={!!e.aggadah_en_approved}
                      testId={`aggadot-row-${e.slug}-aggadah-en-chip`}
                    />
                    <ApprovalChip
                      label="EN · סיפור"
                      approved={!!e.story_en_approved}
                      testId={`aggadot-row-${e.slug}-story-en-chip`}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                <button
                  type="button"
                  data-testid={`aggadot-row-${e.slug}-aggadah-btn`}
                  onClick={() => setOpenDialog({ slug: e.slug, parashaHe: e.he, mode: "aggadah", adminMode: false })}
                  className="inline-flex items-center gap-2 rounded-sm border border-gold/40 hover:bg-gold/10 hover:border-gold text-foreground py-1.5 px-3 text-xs font-heading transition-colors"
                >
                  <Scroll className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                  אגדה · HE
                </button>
                <button
                  type="button"
                  data-testid={`aggadot-row-${e.slug}-story-btn`}
                  onClick={() => setOpenDialog({ slug: e.slug, parashaHe: e.he, mode: "story", adminMode: false })}
                  className="inline-flex items-center gap-2 rounded-sm border border-gold/40 hover:bg-gold/10 hover:border-gold text-foreground py-1.5 px-3 text-xs font-heading transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                  סיפור · HE
                </button>
                <button
                  type="button"
                  data-testid={`aggadot-row-${e.slug}-aggadah-en-btn`}
                  onClick={() => setOpenDialog({ slug: e.slug, parashaHe: e.he, mode: "aggadah", adminMode: true })}
                  className="inline-flex items-center gap-2 rounded-sm border border-emerald-500/40 hover:bg-emerald-500/10 hover:border-emerald-500 text-foreground py-1.5 px-3 text-xs font-heading transition-colors"
                >
                  <Scroll className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.5} />
                  אגדה · EN (סקירה)
                </button>
                <button
                  type="button"
                  data-testid={`aggadot-row-${e.slug}-story-en-btn`}
                  onClick={() => setOpenDialog({ slug: e.slug, parashaHe: e.he, mode: "story", adminMode: true })}
                  className="inline-flex items-center gap-2 rounded-sm border border-emerald-500/40 hover:bg-emerald-500/10 hover:border-emerald-500 text-foreground py-1.5 px-3 text-xs font-heading transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.5} />
                  סיפור · EN (סקירה)
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Missing — to be curated */}
      {missing_entries.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-heading text-lg text-foreground">בהמתנה לאוצור ({missing})</h2>
          <p className="text-xs text-muted-foreground">
            לפרשות אלו עדיין לא נבחר מקור חז״ל. למשתמשי האפליקציה מוצג כרטיס "מקורות חז״ל לפרשה זו יתווספו בקרוב".
          </p>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="aggadot-missing-list">
            {missing_entries.map((m) => (
              <li
                key={m.slug}
                className="border border-foreground/10 rounded-sm bg-card/50 px-3 py-2 text-sm text-muted-foreground flex items-center gap-2"
              >
                <Circle className="h-3.5 w-3.5 text-muted-foreground/60" strokeWidth={1.25} />
                <span>פרשת {m.he}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Shared dialog — opens with the row's mode (aggadah/story) */}
      {openDialog && (
        <ParashaContentDialog
          open={true}
          onClose={() => setOpenDialog(null)}
          slug={openDialog.slug}
          parashaHe={openDialog.parashaHe}
          mode={openDialog.mode}
          adminMode={!!openDialog.adminMode}
          onApprovalChange={() => loadCoverage()}
        />
      )}
    </div>
  );
}

function ApprovalChip({ label, approved, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 text-[10px] font-heading uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
        approved
          ? "text-emerald-700 border-emerald-500/40 bg-emerald-500/5"
          : "text-muted-foreground border-foreground/15 bg-foreground/[0.02]"
      }`}
    >
      {approved ? <ShieldCheck className="h-3 w-3" strokeWidth={1.5} /> : <ShieldAlert className="h-3 w-3" strokeWidth={1.5} />}
      {label} {approved ? "· מאושר" : "· ממתין"}
    </span>
  );
}
